import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import SEO from '../components/SEO';
import AppSidebar from '../components/common/AppSidebar';
import { CalendarIcon, ClockIcon, UserIcon, PhoneIcon, EnvelopeIcon, BuildingOfficeIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import '../styles/layouts/HomePage.scss';

const AdminAppointmentsPage = () => {
    const [appointments, setAppointments] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('appointments'); // 'appointments' or 'enquiries'
    
    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false
    });
    
    // Filter state
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        dateFrom: '',
        dateTo: '',
        legalArea: '' // For enquiries
    });
    
    // Debounced search
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Set data-page attribute on body for CSS targeting (same as homepage)
    useEffect(() => {
        document.body.setAttribute('data-page', 'home');
        
        return () => {
            document.body.removeAttribute('data-page');
        };
    }, []);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(filters.search);
        }, 500);
        
        return () => clearTimeout(timer);
    }, [filters.search]);

    const fetchAppointments = useCallback(async (pageToFetch = pagination.page) => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams({
                page: pageToFetch.toString(),
                limit: pagination.limit.toString(),
                search: debouncedSearch,
                status: filters.status,
                ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
                ...(filters.dateTo && { dateTo: filters.dateTo })
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/appointments?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch appointments');
            }

            const data = await response.json();
            setAppointments(data.appointments || []);
            
            // Normalize pagination structure for appointments
            setPagination({
                page: data.pagination.page,
                limit: data.pagination.limit,
                total: data.pagination.total,
                totalPages: data.pagination.totalPages,
                hasNextPage: data.pagination.hasNextPage,
                hasPrevPage: data.pagination.hasPrevPage
            });
        } catch (err) {
            console.error('Error fetching appointments:', err);
            setError(err.message);
            toast.error('Failed to load appointments');
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, filters.status, filters.dateFrom, filters.dateTo, pagination.limit]);

    const fetchEnquiries = useCallback(async (pageToFetch = pagination.page) => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams({
                page: pageToFetch.toString(),
                limit: pagination.limit.toString(),
                search: debouncedSearch,
                status: filters.status,
                ...(filters.legalArea && { legal_area: filters.legalArea })
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/enquiries?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch enquiries');
            }

            const data = await response.json();
            setEnquiries(data.enquiries || []);
            
            // Use consistent pagination structure
            setPagination({
                page: data.pagination.page,
                limit: data.pagination.limit,
                total: data.pagination.total,
                totalPages: data.pagination.totalPages,
                hasNextPage: data.pagination.hasNextPage,
                hasPrevPage: data.pagination.hasPrevPage
            });
        } catch (err) {
            console.error('Error fetching enquiries:', err);
            setError(err.message);
            toast.error('Failed to load enquiries');
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, filters.status, filters.legalArea, pagination.limit]);

    // Fetch data when dependencies change
    useEffect(() => {
        if (activeTab === 'appointments') {
            fetchAppointments(1); // Start with page 1
        } else {
            fetchEnquiries(1); // Start with page 1
        }
    }, [activeTab, fetchAppointments, fetchEnquiries]);

    // Handle pagination changes
    useEffect(() => {
        if (pagination.page > 1) { // Only fetch if not the initial page
            if (activeTab === 'appointments') {
                fetchAppointments(pagination.page);
            } else {
                fetchEnquiries(pagination.page);
            }
        }
    }, [pagination.page, activeTab, fetchAppointments, fetchEnquiries]);

    // Reset pagination when tab changes and fetch initial data
    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
    }, [activeTab]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setFilters(prev => ({ ...prev, search: '', status: 'all', dateFrom: '', dateTo: '', legalArea: '' }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (claimed) => {
        if (claimed) {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                    Claimed
                </span>
            );
        } else {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                    Unclaimed
                </span>
            );
        }
    };

    return (
        <>
            <SEO 
                fallbackTitle="Appointments Management - Zahoot Admin"
                fallbackDescription="Manage all appointments and client bookings in the Zahoot platform."
            />
            <div className="home-page">
                {/* Sidebar Navigation */}
                <AppSidebar />
                
                <main className="home-page__main">
                    <div className="max-w-7xl w-full px-4 sm:px-6 lg:px-8 mx-auto z-10 relative">
                        
                        {/* Header Section */}
                        <div className="text-center mb-8 z-10 relative pt-8">
                            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                                {activeTab === 'appointments' ? 'Appointments' : 'Enquiries'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D80364] to-[#F0654B]">Management</span>
                            </h1>
                            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                                View and manage all client {activeTab === 'appointments' ? 'appointments and bookings' : 'enquiries and requests'}
                            </p>
                        </div>
                        
                        {/* Tabs Section */}
                        <div className="flex justify-center mb-8">
                            <div className="bg-purple-900/50 rounded-xl p-1 inline-flex">
                                <button
                                    onClick={() => handleTabChange('appointments')}
                                    className={`px-6 py-3 rounded-lg font-medium transition-all ${
                                        activeTab === 'appointments'
                                            ? 'bg-gradient-to-r from-[#D80364] to-[#F0654B] text-white shadow-lg'
                                            : 'text-gray-300 hover:text-white hover:bg-purple-800/50'
                                    }`}
                                >
                                    <CalendarIcon className="h-5 w-5 inline-block mr-2" />
                                    Appointments
                                </button>
                                <button
                                    onClick={() => handleTabChange('enquiries')}
                                    className={`px-6 py-3 rounded-lg font-medium transition-all ${
                                        activeTab === 'enquiries'
                                            ? 'bg-gradient-to-r from-[#D80364] to-[#F0654B] text-white shadow-lg'
                                            : 'text-gray-300 hover:text-white hover:bg-purple-800/50'
                                    }`}
                                >
                                    <DocumentTextIcon className="h-5 w-5 inline-block mr-2" />
                                    Enquiries
                                </button>
                            </div>
                        </div>
                        
                        {/* Filters Section */}
                        <div className="bg-purple-900/50 rounded-xl p-6 shadow-lg mb-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {/* Search */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Search
                                    </label>
                                    <input
                                        type="text"
                                        placeholder={activeTab === 'appointments' ? "Client name, email..." : "Client name, email..."}
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                        className="w-full px-3 py-2 bg-purple-800/50 border border-purple-700/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    />
                                </div>
                                
                                {/* Status Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Status
                                    </label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full px-3 py-2 bg-purple-800/50 border border-purple-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    >
                                        <option value="all">All</option>
                                        <option value="claimed">Claimed</option>
                                        <option value="unclaimed">Unclaimed</option>
                                    </select>
                                </div>
                                
                                {/* Conditional filters based on active tab */}
                                {activeTab === 'appointments' ? (
                                    <>
                                        {/* Date From */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                From Date
                                            </label>
                                            <input
                                                type="date"
                                                value={filters.dateFrom}
                                                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                                className="w-full px-3 py-2 bg-purple-800/50 border border-purple-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                            />
                                        </div>
                                        
                                        {/* Date To */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                To Date
                                            </label>
                                            <input
                                                type="date"
                                                value={filters.dateTo}
                                                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                                className="w-full px-3 py-2 bg-purple-800/50 border border-purple-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* Legal Area Filter for Enquiries */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Legal Area
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., Family Law, Criminal..."
                                                value={filters.legalArea}
                                                onChange={(e) => handleFilterChange('legalArea', e.target.value)}
                                                className="w-full px-3 py-2 bg-purple-800/50 border border-purple-700/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                            />
                                        </div>
                                        
                                        {/* Empty div for spacing */}
                                        <div></div>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        {/* Results Summary */}
                        {!loading && (
                            <div className="mb-6">
                                <p className="text-gray-300">
                                    Showing {activeTab === 'appointments' ? appointments.length : enquiries.length} of {pagination.total} {activeTab === 'appointments' ? 'appointments' : 'enquiries'}
                                </p>
                            </div>
                        )}
                        
                        {/* Content Section */}
                        <div className="bg-purple-900/50 rounded-xl p-6 shadow-lg mb-8">
                            {loading ? (
                                <div className="text-center py-12">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mb-4"></div>
                                    <p className="text-white">Loading {activeTab === 'appointments' ? 'appointments' : 'enquiries'}...</p>
                                </div>
                            ) : error ? (
                                <div className="text-center py-12">
                                    <div className="mx-auto w-16 h-16 flex items-center justify-center bg-red-500/30 rounded-full mb-4">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">Error Loading {activeTab === 'appointments' ? 'Appointments' : 'Enquiries'}</h3>
                                    <p className="text-gray-300 mb-4">{error}</p>
                                    <button
                                        onClick={activeTab === 'appointments' ? fetchAppointments : fetchEnquiries}
                                        className="btn bg-gradient-to-r from-[#D80364] to-[#F0654B] text-white font-medium py-2 px-4 rounded-lg hover:opacity-90 transition-all"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Check if we have data to display */}
                                    {(activeTab === 'appointments' && appointments.length === 0) || (activeTab === 'enquiries' && enquiries.length === 0) ? (
                                        <div className="text-center py-12">
                                            <div className="mx-auto w-16 h-16 flex items-center justify-center bg-purple-600/30 rounded-full mb-4">
                                                {activeTab === 'appointments' ? (
                                                    <CalendarIcon className="h-8 w-8 text-purple-300" />
                                                ) : (
                                                    <DocumentTextIcon className="h-8 w-8 text-purple-300" />
                                                )}
                                            </div>
                                            <h3 className="text-xl font-bold text-white mb-2">No {activeTab === 'appointments' ? 'Appointments' : 'Enquiries'} Found</h3>
                                            <p className="text-gray-300">No {activeTab === 'appointments' ? 'appointments' : 'enquiries'} match your current filters.</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Data Grid */}
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                {activeTab === 'appointments' ? (
                                                    // Appointments Grid
                                                    appointments.map((appointment) => (
                                                        <div key={appointment.id} className="bg-purple-800/50 rounded-lg p-6 border border-purple-700/30 hover:border-purple-600/50 transition-all">
                                                            {/* Header with Status */}
                                                            <div className="flex justify-between items-start mb-4">
                                                                <div>
                                                                    <h3 className="text-lg font-semibold text-white mb-1">
                                                                        Appointment #{appointment.id}
                                                                    </h3>
                                                                    <p className="text-sm text-gray-400">
                                                                        Created {formatDateTime(appointment.created_at)}
                                                                    </p>
                                                                </div>
                                                                {getStatusBadge(appointment.claimed)}
                                                            </div>
                                                            
                                                            {/* Client Info */}
                                                            <div className="space-y-3 mb-4">
                                                                <div className="flex items-center">
                                                                    <UserIcon className="h-4 w-4 text-purple-300 mr-2 flex-shrink-0" />
                                                                    <span className="text-gray-300 text-sm mr-2">Client:</span>
                                                                    <span className="text-white">{appointment.user_name}</span>
                                                                </div>
                                                                
                                                                <div className="flex items-center">
                                                                    <EnvelopeIcon className="h-4 w-4 text-purple-300 mr-2 flex-shrink-0" />
                                                                    <span className="text-gray-300 text-sm mr-2">Email:</span>
                                                                    <span className="text-white">{appointment.user_email}</span>
                                                                </div>
                                                                
                                                                <div className="flex items-center">
                                                                    <PhoneIcon className="h-4 w-4 text-purple-300 mr-2 flex-shrink-0" />
                                                                    <span className="text-gray-300 text-sm mr-2">Phone:</span>
                                                                    <span className="text-white">{appointment.user_phone}</span>
                                                                </div>
                                                                
                                                                <div className="flex items-center">
                                                                    <CalendarIcon className="h-4 w-4 text-purple-300 mr-2 flex-shrink-0" />
                                                                    <span className="text-gray-300 text-sm mr-2">Date:</span>
                                                                    <span className="text-white">{formatDate(appointment.appointment_date)}</span>
                                                                </div>
                                                                
                                                                <div className="flex items-center">
                                                                    <ClockIcon className="h-4 w-4 text-purple-300 mr-2 flex-shrink-0" />
                                                                    <span className="text-gray-300 text-sm mr-2">Time:</span>
                                                                    <span className="text-white">{appointment.start_time || 'N/A'}</span>
                                                                </div>
                                                                
                                                                {appointment.lawyer_firm_name && (
                                                                    <div className="flex items-center">
                                                                        <BuildingOfficeIcon className="h-4 w-4 text-purple-300 mr-2 flex-shrink-0" />
                                                                        <span className="text-gray-300 text-sm mr-2">Firm:</span>
                                                                        <span className="text-white">{appointment.lawyer_firm_name}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            
                                                            {/* Claimed Info */}
                                                            {appointment.claimed === true && appointment.claimed_by_name && (
                                                                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4">
                                                                    <p className="text-green-300 text-sm">
                                                                        <span className="font-medium">Claimed by:</span> {appointment.claimed_by_name}
                                                                    </p>
                                                                    {appointment.claimed_at && (
                                                                        <p className="text-green-400 text-xs mt-1">
                                                                            {formatDateTime(appointment.claimed_at)}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                            
                                                            {/* Actions */}
                                                            <div className="flex flex-wrap gap-2">
                                                                {appointment.user_email && (
                                                                    <a
                                                                        href={`mailto:${appointment.user_email}`}
                                                                        className="btn bg-gradient-to-r from-[#D80364] to-[#F0654B] text-white text-sm py-1.5 px-3 rounded-lg hover:opacity-90 transition-all"
                                                                    >
                                                                        Email Client
                                                                    </a>
                                                                )}
                                                                
                                                                {appointment.user_phone && (
                                                                    <a
                                                                        href={`tel:${appointment.user_phone}`}
                                                                        className="btn bg-white text-gray-800 text-sm py-1.5 px-3 rounded-lg hover:bg-gray-100 transition-all"
                                                                    >
                                                                        Call Client
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    // Enquiries Grid
                                                    enquiries.map((enquiry) => (
                                                        <div key={enquiry.id} className="bg-purple-800/50 rounded-lg p-6 border border-purple-700/30 hover:border-purple-600/50 transition-all">
                                                            {/* Header with Status */}
                                                            <div className="flex justify-between items-start mb-4">
                                                                <div>
                                                                    <h3 className="text-lg font-semibold text-white mb-1">
                                                                        Enquiry #{enquiry.id}
                                                                    </h3>
                                                                    <p className="text-sm text-gray-400">
                                                                        Created {formatDateTime(enquiry.created_at)}
                                                                    </p>
                                                                </div>
                                                                {getStatusBadge(enquiry.claimed)}
                                                            </div>
                                                            
                                                            {/* Client Info */}
                                                            <div className="space-y-3 mb-4">
                                                                <div className="flex items-center">
                                                                    <UserIcon className="h-4 w-4 text-purple-300 mr-2 flex-shrink-0" />
                                                                    <span className="text-gray-300 text-sm mr-2">Client:</span>
                                                                    <span className="text-white">{enquiry.user_name}</span>
                                                                </div>
                                                                
                                                                <div className="flex items-center">
                                                                    <EnvelopeIcon className="h-4 w-4 text-purple-300 mr-2 flex-shrink-0" />
                                                                    <span className="text-gray-300 text-sm mr-2">Email:</span>
                                                                    <span className="text-white">{enquiry.user_email}</span>
                                                                </div>
                                                                
                                                                <div className="flex items-center">
                                                                    <PhoneIcon className="h-4 w-4 text-purple-300 mr-2 flex-shrink-0" />
                                                                    <span className="text-gray-300 text-sm mr-2">Phone:</span>
                                                                    <span className="text-white">{enquiry.user_phone}</span>
                                                                </div>
                                                                
                                                                <div className="flex items-center">
                                                                    <DocumentTextIcon className="h-4 w-4 text-purple-300 mr-2 flex-shrink-0" />
                                                                    <span className="text-gray-300 text-sm mr-2">Legal Area:</span>
                                                                    <span className="text-white">{enquiry.legal_area}</span>
                                                                </div>
                                                                
                                                                {/* Description */}
                                                                <div className="mt-3">
                                                                    <p className="text-gray-300 text-sm mb-1">Description:</p>
                                                                    <p className="text-white text-sm bg-purple-700/30 p-3 rounded-lg">
                                                                        {enquiry.description && enquiry.description.length > 150 
                                                                            ? `${enquiry.description.substring(0, 150)}...` 
                                                                            : enquiry.description || 'No description provided'
                                                                        }
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Claimed Info */}
                                                            {enquiry.claimed === true && enquiry.claimed_by_name && (
                                                                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4">
                                                                    <p className="text-green-300 text-sm">
                                                                        <span className="font-medium">Claimed by:</span> {enquiry.claimed_by_name}
                                                                    </p>
                                                                    {enquiry.claimed_at && (
                                                                        <p className="text-green-400 text-xs mt-1">
                                                                            {formatDateTime(enquiry.claimed_at)}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                            
                                                            {/* Actions */}
                                                            <div className="flex flex-wrap gap-2">
                                                                {enquiry.user_email && (
                                                                    <a
                                                                        href={`mailto:${enquiry.user_email}`}
                                                                        className="btn bg-gradient-to-r from-[#D80364] to-[#F0654B] text-white text-sm py-1.5 px-3 rounded-lg hover:opacity-90 transition-all"
                                                                    >
                                                                        Email Client
                                                                    </a>
                                                                )}
                                                                
                                                                {enquiry.user_phone && (
                                                                    <a
                                                                        href={`tel:${enquiry.user_phone}`}
                                                                        className="btn bg-white text-gray-800 text-sm py-1.5 px-3 rounded-lg hover:bg-gray-100 transition-all"
                                                                    >
                                                                        Call Client
                                                                    </a>
                                                                )}
                                                                
                                                                {enquiry.claimed === false && (
                                                                    <a
                                                                        href={`/claim-enquiry/${enquiry.claim_token}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="btn bg-purple-600 text-white text-sm py-1.5 px-3 rounded-lg hover:bg-purple-700 transition-all"
                                                                    >
                                                                        View Claim Link
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                            
                                            {/* Pagination */}
                                            {pagination.totalPages > 1 && (
                                                <div className="flex justify-center items-center mt-8 space-x-2">
                                                    <button
                                                        onClick={() => handlePageChange(pagination.page - 1)}
                                                        disabled={!pagination.hasPrevPage}
                                                        className="btn bg-purple-800/50 text-white py-2 px-4 rounded-lg hover:bg-purple-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                                    >
                                                        Previous
                                                    </button>
                                                    
                                                    <span className="text-gray-300 px-4">
                                                        Page {pagination.page} of {pagination.totalPages}
                                                    </span>
                                                    
                                                    <button
                                                        onClick={() => handlePageChange(pagination.page + 1)}
                                                        disabled={!pagination.hasNextPage}
                                                        className="btn bg-purple-800/50 text-white py-2 px-4 rounded-lg hover:bg-purple-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        
                        {/* Back to Admin Dashboard */}
                        <div className="text-center">
                            <Link 
                                to="/admin" 
                                className="text-gray-300 hover:text-white transition-colors"
                            >
                                ← Back to Admin Dashboard
                            </Link>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default AdminAppointmentsPage; 